import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { toast } from 'sonner'
import { BRAND_NAME } from '@/lib/branding'

interface SiteSettingsFormProps {
  settings: Record<string, unknown>
}

const NOTIFICATION_FROM_DOMAIN = 'itsonenumber.com'
const DEFAULT_NOTIFICATION_FROM_LOCAL_PART = 'notify'

function decodeLegacySettingValue(value: unknown): unknown {
  let result: unknown = value

  for (let i = 0; i < 2; i++) {
    if (typeof result !== 'string') break

    const trimmed = result.trim()
    if (!trimmed) break

    const looksJson =
      (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
      trimmed === 'true' ||
      trimmed === 'false' ||
      trimmed === 'null' ||
      /^-?\d+(\.\d+)?$/.test(trimmed)

    if (!looksJson) break

    try {
      result = JSON.parse(trimmed)
    } catch {
      break
    }
  }

  return result
}

function getSettingString(value: unknown, fallback = ''): string {
  const decoded = decodeLegacySettingValue(value)
  return typeof decoded === 'string' ? decoded : fallback
}

function getSettingObject(value: unknown): Record<string, string> {
  const decoded = decodeLegacySettingValue(value)
  return decoded && typeof decoded === 'object'
    ? (decoded as Record<string, string>)
    : {}
}

function sanitizeEmailLocalPart(value: string): string {
  const [rawLocalPart = ''] = value.trim().toLowerCase().split('@')
  return rawLocalPart.replace(/[^a-z0-9._+-]/g, '')
}

function toNotificationFromEmail(localPartValue: string): string {
  const localPart = sanitizeEmailLocalPart(localPartValue) || DEFAULT_NOTIFICATION_FROM_LOCAL_PART
  return `${localPart}@${NOTIFICATION_FROM_DOMAIN}`
}

function getNotificationFromLocalPart(value: unknown): string {
  const configured = getSettingString(value)
  const localPart = sanitizeEmailLocalPart(configured)
  return localPart || DEFAULT_NOTIFICATION_FROM_LOCAL_PART
}

export function SiteSettingsForm({ settings }: SiteSettingsFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const socialLinks = getSettingObject(settings.social_links)
  const defaultContactEmail = getSettingString(settings.contact_email, 'admin@schedulekey.com')
  const [formData, setFormData] = useState({
    site_title: getSettingString(settings.site_title, BRAND_NAME),
    site_description: getSettingString(settings.site_description),
    hero_headline: getSettingString(settings.hero_headline, "This Week's Featured Deal"),
    hero_subheadline: getSettingString(settings.hero_subheadline),
    archive_headline: getSettingString(settings.archive_headline, 'Past Reviews'),
    archive_subheadline: getSettingString(settings.archive_subheadline),
    contact_email: defaultContactEmail,
    notification_from_local_part: getNotificationFromLocalPart(settings.notification_from_email),
    notification_reply_to_email: getSettingString(settings.notification_reply_to_email, defaultContactEmail),
    footer_text: getSettingString(settings.footer_text),
    youtube_url: socialLinks.youtube || '',
    twitter_url: socialLinks.twitter || '',
    instagram_url: socialLinks.instagram || '',
  })

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const updates = [
        { key: 'site_title', value: formData.site_title },
        { key: 'site_description', value: formData.site_description },
        { key: 'hero_headline', value: formData.hero_headline },
        { key: 'hero_subheadline', value: formData.hero_subheadline },
        { key: 'archive_headline', value: formData.archive_headline },
        { key: 'archive_subheadline', value: formData.archive_subheadline },
        { key: 'contact_email', value: formData.contact_email },
        { key: 'notification_from_email', value: toNotificationFromEmail(formData.notification_from_local_part) },
        { key: 'notification_reply_to_email', value: formData.notification_reply_to_email },
        { key: 'footer_text', value: formData.footer_text },
        {
          key: 'social_links',
          value: {
            youtube: formData.youtube_url,
            twitter: formData.twitter_url,
            instagram: formData.instagram_url,
          },
        },
      ]

      for (const update of updates) {
        const settingData = { key: update.key, value: update.value }
        const { error } = await (supabase
          .from('site_settings') as unknown as { upsert: (data: typeof settingData, options: { onConflict: string }) => Promise<{ error: Error | null }> })
          .upsert(settingData, { onConflict: 'key' })
        if (error) throw error
      }

      toast.success('Settings saved successfully')
    } catch (error) {
      console.error('Settings error:', error)
      toast.error('Failed to save settings')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* General */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">General</h3>

        <div className="space-y-2">
          <Label htmlFor="site_title">Site Title</Label>
          <Input
            id="site_title"
            value={formData.site_title}
            onChange={(e) => handleChange('site_title', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="site_description">Site Description</Label>
          <Textarea
            id="site_description"
            value={formData.site_description}
            onChange={(e) => handleChange('site_description', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="contact_email">Contact Email</Label>
          <Input
            id="contact_email"
            type="email"
            value={formData.contact_email}
            onChange={(e) => handleChange('contact_email', e.target.value)}
          />
          <p className="text-sm text-muted-foreground">
            Receives contact form submissions and admin-facing notifications.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="notification_from_local_part">Notification From Email</Label>
          <div className="overflow-hidden rounded-md border border-input bg-background sm:flex sm:items-center">
            <Input
              id="notification_from_local_part"
              type="text"
              value={formData.notification_from_local_part}
              onChange={(e) => handleChange('notification_from_local_part', sanitizeEmailLocalPart(e.target.value))}
              placeholder={DEFAULT_NOTIFICATION_FROM_LOCAL_PART}
              className="border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 sm:rounded-none"
            />
            <span className="block border-t px-3 py-2 text-sm text-muted-foreground sm:border-t-0 sm:border-l">
              @{NOTIFICATION_FROM_DOMAIN}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Sender domain is fixed. Only the part before @ can be changed.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="notification_reply_to_email">Notification Reply-To Email</Label>
          <Input
            id="notification_reply_to_email"
            type="email"
            value={formData.notification_reply_to_email}
            onChange={(e) => handleChange('notification_reply_to_email', e.target.value)}
            placeholder="admin@schedulekey.com"
          />
          <p className="text-sm text-muted-foreground">
            Used as reply-to for outgoing emails when a message-specific reply address is not set.
          </p>
        </div>
      </div>

      <Separator />

      {/* Homepage */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Homepage</h3>

        <div className="space-y-2">
          <Label htmlFor="hero_headline">Hero Headline</Label>
          <Input
            id="hero_headline"
            value={formData.hero_headline}
            onChange={(e) => handleChange('hero_headline', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="hero_subheadline">Hero Subheadline</Label>
          <Textarea
            id="hero_subheadline"
            value={formData.hero_subheadline}
            onChange={(e) => handleChange('hero_subheadline', e.target.value)}
          />
        </div>
      </div>

      <Separator />

      {/* Archive Page */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Past Reviews Page</h3>

        <div className="space-y-2">
          <Label htmlFor="archive_headline">Past Reviews Headline</Label>
          <Input
            id="archive_headline"
            value={formData.archive_headline}
            onChange={(e) => handleChange('archive_headline', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="archive_subheadline">Past Reviews Subheadline</Label>
          <Textarea
            id="archive_subheadline"
            value={formData.archive_subheadline}
            onChange={(e) => handleChange('archive_subheadline', e.target.value)}
          />
        </div>
      </div>

      <Separator />

      {/* Social Links */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Social Links</h3>

        <div className="space-y-2">
          <Label htmlFor="youtube_url">YouTube URL</Label>
          <Input
            id="youtube_url"
            value={formData.youtube_url}
            onChange={(e) => handleChange('youtube_url', e.target.value)}
            placeholder="https://youtube.com/@..."
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="twitter_url">Twitter/X URL</Label>
          <Input
            id="twitter_url"
            value={formData.twitter_url}
            onChange={(e) => handleChange('twitter_url', e.target.value)}
            placeholder="https://twitter.com/..."
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="instagram_url">Instagram URL</Label>
          <Input
            id="instagram_url"
            value={formData.instagram_url}
            onChange={(e) => handleChange('instagram_url', e.target.value)}
            placeholder="https://instagram.com/..."
          />
        </div>
      </div>

      <Separator />

      {/* Footer */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Footer</h3>

        <div className="space-y-2">
          <Label htmlFor="footer_text">Footer Text</Label>
          <Input
            id="footer_text"
            value={formData.footer_text}
            onChange={(e) => handleChange('footer_text', e.target.value)}
          />
        </div>
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
        {isSubmitting ? 'Saving...' : 'Save Settings'}
      </Button>
    </form>
  )
}
